import React from "react";
import { type NextPage } from "next";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import { CustomerStatsHeroAnimation, GradientHalfHero } from "components/hero";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import {
  DestinationMarquee,
  IndustryLeaderAwards,
  SecurityFeatures,
  TraditionalCdpVsHightouch,
} from "components/content";
import { StackedLogos } from "components/logos";
import { BookDemoButton } from "components/event-tracking";
import { DataDemoEmbed, DataDiptychs, DataWarehouseStrategy } from "components/solutions/data";
import { implementationEffortComparison } from "components/content/traditional-cdp-vs-hightouch/data";
import { AuraPlate } from "ui/plates";
import heroImage from "../../../public/assets/solutions/data/hero.png";

const Data: NextPage<LandingPageProps> = ({ page, defaultLogos, customers, destinations, announcementBanner }) => {
  const calendly = getSafeCustomer(customers, "calendly");
  const ramp = getSafeCustomer(customers, "ramp");
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.violet.150"
      footerOptions={{ variant: "violet" }}
    >
      <GradientHalfHero
        variant="violet"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        mediaMaxW={{ base: "100%", lg: "440px", xl: "480px" }}
        media={
          <CustomerStatsHeroAnimation
            bgPhoto={{
              src: heroImage,
              alt: "An single person smiling and using a laptop.",
            }}
            primaryCaseStudyStat={{
              customer: calendly,
              percentValue: 16,
              description: "Boost in acquisition rate",
            }}
            secondaryCaseStudyStat={{
              customer: ramp,
              percentValue: 25,
              description: "Sales driven by data-powered outbound automation",
            }}
          />
        }
        mb={{ sm: "small", md: "big", lg: 0 }}
      />

      <StackedLogos customers={defaultLogos} title="Trusted by leading data-driven companies" my="0" />

      <DataWarehouseStrategy />

      <AuraPlate condensed variant="violet">
        <CenteredQuote
          maxW="860px"
          quoteText="Implementing and onboarding Hightouch took less than a day from start to finish to have data flowing out of BigQuery to our downstream destinations."
          author={{ name: "Omar Mayar", job: "Sr. Data Engineer" }}
          company={{
            name: "Calendly",
            logo: calendly.logoUrl,
          }}
          cta={{ href: "/customers/calendly", text: "Read the story" }}
        />
      </AuraPlate>

      <DataDemoEmbed />

      <TraditionalCdpVsHightouch
        titleMaxW="700px"
        title="The CDP loved by marketers and trusted by data teams"
        description="Don’t put yourself through a long and painful implementation with a traditional CDP, just to fail months later. Bring your own schema and use your existing data to enable data-driven growth."
        actions={<BookDemoButton text="Talk to an expert" />}
        rows={[
          {
            category: "Data storage and governance",
            traditionalCdp: {
              title: "Copies data outside of your infrastructure",
              description:
                "Traditional CDPs are built on duplicative data storage - your database and theirs. This increases your data governance risks and creates hurdles for compliance.",
            },
            hightouch: {
              title: "Doesn’t store data and offers robust governance",
              description:
                "Hightouch operates within your data infrastructure, vastly simplifying compliance and data governance. You have granular control over how data can and cannot be used.",
            },
          },
          {
            category: "Data flexibility",
            traditionalCdp: {
              title: "Operates on a narrow silo of customer behaviors",
              description:
                "Traditional CDPs were built to handle user and behavioral event data from websites and mobile apps. It’s expensive to integrate data from your other systems, or organize it into non-user entities like insurance policies.",
            },
            hightouch: {
              title: "Takes action on any permitted data source",
              description:
                "Your data warehouse can ingest, organize, and govern data from any of your systems, for any entities that matter to your business and customers. Hightouch enables you to act on your complete data.",
            },
          },
          implementationEffortComparison,
        ]}
      />

      <SecurityFeatures titleAs="h3" />

      <DataDiptychs />

      <IndustryLeaderAwards />

      <DestinationMarquee destinations={destinations} />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/solutions/data", {
  loadDefaultLogos: true,
  loadDestinations: "all",
  loadCustomers: ["calendly", "ramp"],
});

export default Data;
